import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Modal,
  Box,
  Paper,
  Typography,
  IconButton,
  TextField,
  Grid,
  MenuItem,
  Button,
  CircularProgress,
} from "@mui/material";
import { Close as CloseIcon, Search as SearchIcon } from "@mui/icons-material";

import { getToken, base_url } from "../../../config/config";

const ANRModal = ({ open, onClose, onSelect, isDarkMode }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [projects, setProjects] = useState([]);
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [latestProject, setLatestProject] = useState(null);
  const [availableYears, setAvailableYears] = useState([23, 24, 25]);
  const token = getToken();

  // Debounced fetch function
  const debouncedFetch = useCallback(
    debounce((searchTerm, year, month) => {
      fetchProjects(searchTerm, year, month);
    }, 500),
    [],
  );

  useEffect(() => {
    if (!open) {
      // Reset filters when modal closes
      setSearchTerm("");
      setYear("");
      setMonth("");
      setProjects([]);
      setLatestProject(null);
      setError(null);
      return;
    }
    // Initial fetch to get all projects and available years
    fetchProjects("", "", "");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // Trigger backend call when filters change
    debouncedFetch(searchTerm, year, month);
  }, [searchTerm, year, month, debouncedFetch, open]);

  // Simple debounce function
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  const fetchProjects = async (
    search = "",
    selectedYear = "",
    selectedMonth = "",
  ) => {
    setLoading(true);
    setError(null);
    try {
      // Build query parameters
      const params = new URLSearchParams({
        page: "1",
        limit: "50", // Increased limit to get more results
      });

      if (search.trim()) {
        params.append("search", search.trim());
      }
      if (selectedYear) {
        params.append("year", selectedYear);
      }
      if (selectedMonth) {
        params.append("month", selectedMonth);
      }

      const res = await axios.get(
        `${base_url}/v1/getAllProjects?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (res.data.status === 1) {
        const all = res.data.data || [];
        setProjects(all);

        if (all.length > 0) {
          const sorted = all.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
          );
          setLatestProject(sorted[0]);
        } else {
          setLatestProject(null);
          if (search || selectedYear || selectedMonth) {
            setError("Keine Projekte mit den angegebenen Filtern gefunden.");
          } else {
            setError("Keine Projekte gefunden.");
          }
        }
      } else {
        setError("Fehler beim Abruf der Projekte.");
      }
    } catch (err) {
      console.error("Error fetching projects:", err);
      setError("Fehler beim Abruf der Projekte.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProject = (project) => {
    onSelect({
      anr: project.ANR,
      auftragsbezeichnung: project.Auftragsbezeichnung,
      maxLohnaufwand: project.MaxLohnaufwand || 0,
      projectId: project._id,
      menge: project.Menge,
      mengeCost: project.MengeCost,
    });
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", sm: "80%", md: "60%" },
          maxWidth: "800px",
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: 2,
          outline: "none",
        }}
      >
        <Paper sx={{ width: "100%", height: "100%" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 2,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="h6">ANR Auswählen</Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 3 }}>
            {/* Searching */}
            <TextField
              label="Suche nach ANR oder Bezeichnung"
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1 }} />,
              }}
              sx={{ mb: 2 }}
              disabled={loading}
            />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Jahr"
                  select
                  fullWidth
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  disabled={loading}
                >
                  <MenuItem value="">Alle Jahre</MenuItem>
                  {availableYears.map((y) => (
                    <MenuItem key={y} value={String(y)}>{`20${y}`}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Monat"
                  select
                  fullWidth
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  disabled={loading}
                >
                  <MenuItem value="">Alle Monate</MenuItem>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <MenuItem key={m} value={String(m).padStart(2, "0")}>
                      {new Date(0, m - 1).toLocaleString("de-DE", {
                        month: "long",
                      })}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            {/* Loading / Error */}
            {loading && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 2,
                }}
              >
                <CircularProgress size={24} sx={{ mr: 1 }} />
                <Typography variant="body2">
                  Projekte werden geladen...
                </Typography>
              </Box>
            )}
            {error && (
              <Box
                sx={{ mt: 2, p: 2, bgcolor: "error.light", borderRadius: 1 }}
              >
                <Typography variant="body2">{error}</Typography>
              </Box>
            )}

            {/* Latest project */}
            {!loading && latestProject && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Neuestes Projekt
                </Typography>
                <Button
                  variant="contained"
                  sx={{ mt: 1 }}
                  onClick={() => handleSelectProject(latestProject)}
                >
                  {latestProject.ANR} - {latestProject.Auftragsbezeichnung}
                </Button>
              </Box>
            )}

            {!loading && !error && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Nutzen Sie die Suchleiste und Filter für präzise Ergebnisse
                </Typography>
              </Box>
            )}

            {/* Filtered results */}
            {!loading && projects.length > 0 && (
              <Box
                sx={{
                  mt: 2,
                  maxHeight: 300,
                  overflowY: "auto",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                {projects.map((p) => (
                  <Button
                    key={p._id}
                    variant="outlined"
                    fullWidth
                    sx={{
                      mb: 1,
                      justifyContent: "flex-start",
                      mx: 1,
                      width: "calc(100% - 16px)",
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                    onClick={() => handleSelectProject(p)}
                  >
                    <Box sx={{ textAlign: "left", width: "100%" }}>
                      <Typography variant="subtitle2" noWrap>
                        {p.ANR}
                      </Typography>
                      <Typography
                        variant="caption"
                        noWrap
                        color="text.secondary"
                      >
                        {p.Auftragsbezeichnung || "Keine Bezeichnung"}
                      </Typography>
                    </Box>
                  </Button>
                ))}
              </Box>
            )}

            {/* No results message */}
            {!loading &&
              !error &&
              projects.length === 0 &&
              (searchTerm || year || month) && (
                <Box
                  sx={{
                    mt: 2,
                    p: 3,
                    textAlign: "center",
                    bgcolor: "action.hover",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Keine Projekte gefunden. Versuchen Sie andere Suchkriterien.
                  </Typography>
                </Box>
              )}

            <Box sx={{ textAlign: "right", mt: 3 }}>
              <Button
                variant="outlined"
                onClick={onClose}
                startIcon={<CloseIcon />}
              >
                Abbrechen
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Modal>
  );
};

export default ANRModal;
